import { supabase } from '../lib/supabase';
import { getCurrentUserId } from '../utils/authUtils';
import { deriveInsightsFromResume } from './journeyProfileInsightsService';
import { parseResumeSections } from '../../aiResumeOptimizer/utils/parseResumeSections';

export type ObjectiveStatus = 'active' | 'completed' | 'paused';
export type GoalStatus = 'active' | 'completed' | 'paused';
export type TaskType = 'optimize' | 'apply' | 'prep' | 'custom';
export type JourneySource = 'auto' | 'user';

export interface UserObjective {
  id: string;
  user_id: string;
  title: string;
  target_date: string | null;
  source: JourneySource;
  status: ObjectiveStatus;
  created_at: string;
  updated_at: string;
}

export interface UserGoal {
  id: string;
  user_id: string;
  objective_id: string | null;
  title: string;
  source: JourneySource;
  status: GoalStatus;
  created_at: string;
  updated_at: string;
}

export interface UserTask {
  id: string;
  user_id: string;
  goal_id: string | null;
  title: string;
  task_type: TaskType;
  due_date: string | null;
  completed_at: string | null;
  source: JourneySource;
  created_at: string;
  updated_at: string;
}

export interface UserStreak {
  user_id: string;
  current_streak_days: number;
  last_activity_date: string | null;
  longest_streak_days: number;
  updated_at: string;
}

export interface UserProfileInsights {
  user_id: string;
  skills: string[];
  strengths: string[];
  weaknesses: string[];
  focus_areas: string[];
  technical_strengths: string[];
  experience_level: string | null;
  industry: string | null;
  hobbies: string | null;
  preferences: Record<string, unknown>;
  target_role: string | null;
  target_date: string | null;
  job_hunt_intensity: 'light' | 'moderate' | 'all-in' | null;
  last_parsed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface JourneyData {
  objective: UserObjective | null;
  goals: UserGoal[];
  tasks: UserTask[];
  completedTasks: UserTask[];
  streak: UserStreak | null;
  insights: UserProfileInsights | null;
}

async function ensureUserId(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (user?.id) return user.id;
  const fallback = await getCurrentUserId();
  if (fallback) return fallback;
  throw new Error('Not authenticated');
}

export async function getJourneyData(): Promise<JourneyData> {
  const userId = await ensureUserId();

  const [objectivesRes, goalsRes, tasksRes, completedTasksRes, streakRes, insightsRes] = await Promise.all([
    supabase
      .from('ggj_user_objectives')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle(),
    supabase
      .from('ggj_user_goals')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'active')
      .order('created_at', { ascending: true }),
    supabase
      .from('ggj_user_tasks')
      .select('*')
      .eq('user_id', userId)
      .is('completed_at', null)
      .order('due_date', { ascending: true, nullsFirst: false }),
    supabase
      .from('ggj_user_tasks')
      .select('*')
      .eq('user_id', userId)
      .not('completed_at', 'is', null)
      .order('completed_at', { ascending: false })
      .limit(15),
    supabase
      .from('ggj_user_streak')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle(),
    supabase
      .from('ggj_user_profile_insights')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle(),
  ]);

  const objective = objectivesRes.data as UserObjective | null;
  const goals = (goalsRes.data ?? []) as UserGoal[];
  const tasks = (tasksRes.data ?? []) as UserTask[];
  const completedTasks = (completedTasksRes.data ?? []) as UserTask[];
  const streak = streakRes.data as UserStreak | null;
  const insights = insightsRes.data as UserProfileInsights | null;

  return { objective, goals, tasks, completedTasks, streak, insights };
}

export async function getTasksDueTodayAndThisWeek(): Promise<UserTask[]> {
  const userId = await ensureUserId();
  const today = new Date().toISOString().slice(0, 10);
  const endOfWeek = new Date();
  endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));
  const weekEnd = endOfWeek.toISOString().slice(0, 10);

  const { data, error } = await supabase
    .from('ggj_user_tasks')
    .select('*')
    .eq('user_id', userId)
    .is('completed_at', null)
    .or(`due_date.lte.${weekEnd},due_date.is.null`)
    .order('due_date', { ascending: true, nullsFirst: true });

  if (error) return [];
  const tasks = (data ?? []) as UserTask[];
  return tasks.filter((t) => !t.due_date || t.due_date <= weekEnd);
}

export async function completeTask(taskId: string): Promise<void> {
  const userId = await ensureUserId();
  const now = new Date().toISOString();

  const { error: updateError } = await supabase
    .from('ggj_user_tasks')
    .update({ completed_at: now, updated_at: now })
    .eq('id', taskId)
    .eq('user_id', userId);

  if (updateError) throw updateError;

  await updateStreakOnActivity(userId);
}

async function updateStreakOnActivity(userId: string): Promise<void> {
  const today = new Date().toISOString().slice(0, 10);
  const { data: existing } = await supabase
    .from('ggj_user_streak')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  const last = (existing?.last_activity_date ?? null) as string | null;
  const currentStreak = (existing?.current_streak_days ?? 0) as number;
  const longest = (existing?.longest_streak_days ?? 0) as number;

  let newStreak = currentStreak;
  if (!last) {
    newStreak = 1;
  } else {
    const lastDate = new Date(last);
    const todayDate = new Date(today);
    const diffDays = Math.round((todayDate.getTime() - lastDate.getTime()) / (24 * 60 * 60 * 1000));
    if (diffDays === 0) newStreak = currentStreak;
    else if (diffDays === 1) newStreak = currentStreak + 1;
    else newStreak = 1;
  }
  const newLongest = Math.max(longest, newStreak);

  await supabase
    .from('ggj_user_streak')
    .upsert(
      {
        user_id: userId,
        current_streak_days: newStreak,
        last_activity_date: today,
        longest_streak_days: newLongest,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' }
    );
}

export async function addObjective(title: string, targetDate?: string | null): Promise<UserObjective> {
  const userId = await ensureUserId();
  const row = {
    user_id: userId,
    title,
    target_date: targetDate || null,
    source: 'user' as JourneySource,
    status: 'active' as ObjectiveStatus,
  };
  const { data, error } = await supabase.from('ggj_user_objectives').insert(row).select().single();
  if (error) throw error;
  return data as UserObjective;
}

export async function addGoal(title: string, objectiveId?: string | null): Promise<UserGoal> {
  const userId = await ensureUserId();
  const row = {
    user_id: userId,
    objective_id: objectiveId || null,
    title,
    source: 'user' as JourneySource,
    status: 'active' as GoalStatus,
  };
  const { data, error } = await supabase.from('ggj_user_goals').insert(row).select().single();
  if (error) throw error;
  return data as UserGoal;
}

export async function addTask(
  title: string,
  options?: { goalId?: string | null; dueDate?: string | null; taskType?: TaskType }
): Promise<UserTask> {
  const userId = await ensureUserId();
  const row = {
    user_id: userId,
    goal_id: options?.goalId ?? null,
    title,
    task_type: options?.taskType ?? 'custom',
    due_date: options?.dueDate ?? null,
    completed_at: null,
    source: 'user' as JourneySource,
  };
  const { data, error } = await supabase.from('ggj_user_tasks').insert(row).select().single();
  if (error) throw error;
  return data as UserTask;
}

export async function updateObjective(
  id: string,
  updates: { title?: string; target_date?: string | null; status?: ObjectiveStatus }
): Promise<void> {
  const userId = await ensureUserId();
  const { error } = await supabase
    .from('ggj_user_objectives')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .eq('user_id', userId);
  if (error) throw error;
}

export async function updateGoal(id: string, updates: { title?: string; status?: GoalStatus }): Promise<void> {
  const userId = await ensureUserId();
  const { error } = await supabase
    .from('ggj_user_goals')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .eq('user_id', userId);
  if (error) throw error;
}

export async function upsertProfileInsights(insights: {
  target_role?: string | null;
  target_date?: string | null;
  job_hunt_intensity?: 'light' | 'moderate' | 'all-in' | null;
  skills?: string[];
  strengths?: string[];
  weaknesses?: string[];
  focus_areas?: string[];
  technical_strengths?: string[];
  experience_level?: string | null;
  industry?: string | null;
  last_parsed_at?: string | null;
}): Promise<UserProfileInsights> {
  const userId = await ensureUserId();
  const now = new Date().toISOString();
  const row = {
    user_id: userId,
    skills: insights.skills ?? [],
    strengths: insights.strengths ?? [],
    weaknesses: insights.weaknesses ?? [],
    focus_areas: insights.focus_areas ?? [],
    technical_strengths: insights.technical_strengths ?? [],
    experience_level: insights.experience_level ?? null,
    industry: insights.industry ?? null,
    target_role: insights.target_role ?? null,
    target_date: insights.target_date ?? null,
    job_hunt_intensity: insights.job_hunt_intensity ?? null,
    last_parsed_at: insights.last_parsed_at ?? null,
    updated_at: now,
  };
  const { data, error } = await supabase
    .from('ggj_user_profile_insights')
    .upsert(row, { onConflict: 'user_id' })
    .select()
    .single();
  if (error) throw error;
  return data as UserProfileInsights;
}

export async function getProfileInsights(): Promise<UserProfileInsights | null> {
  const userId = await ensureUserId();
  const { data } = await supabase
    .from('ggj_user_profile_insights')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  return data as UserProfileInsights | null;
}

/**
 * Save the user's uploaded resume to user_resumes (parsed content). File is stored in
 * Supabase storage bucket "user-resumes"; this stores the parsed text/sections for word cloud.
 * Call when user uploads in the optimize flow (authenticated).
 */
export async function saveUserResume(payload: {
  file_name: string;
  file_type?: string;
  parsed_text: string;
  sections?: unknown;
  structured?: unknown;
}): Promise<void> {
  const userId = await ensureUserId();
  await supabase.from('ggj_user_resumes').insert({
    user_id: userId,
    original_file: payload.file_name,
    format: payload.file_type ?? null,
    upload_timestamp: new Date().toISOString(),
    parsed_json: {
      rawText: payload.parsed_text || '',
      structured: payload.structured ?? payload.sections ?? null,
    },
  });
}

/**
 * When profile insights are empty, derive from the user's stored resume (user_resumes table) first.
 * Uses LangChain-style structured parse (Skills, Technical Proficiencies) when available, same as resume upload.
 */
export async function backfillProfileInsightsFromLatestOptimization(): Promise<boolean> {
  try {
    const userId = await ensureUserId();
    const { data: resumeList } = await supabase
      .from('ggj_user_resumes')
      .select('parsed_json')
      .eq('user_id', userId)
      .order('upload_timestamp', { ascending: false })
      .limit(1);
    const row = resumeList?.[0] ?? null;
    const parsed = (row as { parsed_json?: { rawText?: string; structured?: Record<string, unknown> } } | null)?.parsed_json;
    const textFromResume = typeof parsed?.rawText === 'string' ? parsed.rawText.trim() : '';
    const structured = parsed?.structured;
    const hasStructuredSkills =
      structured &&
      typeof structured === 'object' &&
      (Array.isArray((structured as any).Skills) || Array.isArray((structured as any)['Technical Proficiencies']));

    if (textFromResume) {
      await deriveInsightsFromResume({
        rawText: textFromResume,
        sections: parseResumeSections(textFromResume),
        structured: hasStructuredSkills ? (structured as any) : undefined,
      });
      return true;
    }
    const { data: optRow } = await supabase
      .from('ggj_llm_optimization_level2')
      .select('optimized_resume')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    const textFromOpt = (optRow as { optimized_resume?: string } | null)?.optimized_resume?.trim();
    if (!textFromOpt) return false;
    await deriveInsightsFromResume({
      rawText: textFromOpt,
      sections: parseResumeSections(textFromOpt),
    });
    return true;
  } catch {
    return false;
  }
}

export async function pauseActiveObjectives(): Promise<void> {
  const userId = await ensureUserId();
  const { data: rows } = await supabase
    .from('ggj_user_objectives')
    .select('id')
    .eq('user_id', userId)
    .eq('status', 'active');
  if (!rows?.length) return;
  const now = new Date().toISOString();
  for (const row of rows) {
    await supabase
      .from('ggj_user_objectives')
      .update({ status: 'paused', updated_at: now })
      .eq('id', row.id)
      .eq('user_id', userId);
  }
}
